import { useEffect, useRef, useState } from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import { MASKED_CREDENTIAL } from "../../models/company";

type OfficeKeys = Record<string, string>;

interface OfficeKeyRow {
  id: number;
  label: string;
  key: string;
  // Label of this office as stored in Compass; undefined for rows added in this session.
  storedLabel?: string;
}

interface OfficeKeysEditorProps {
  // Current api_key in the company state (office label -> key, keys masked when unchanged).
  value: unknown;
  // api_key as loaded from Compass (office label -> masked key).
  originalValue: unknown;
  onChange: (value: OfficeKeys | null) => void;
  // Reports the validation error of changed office keys (null when valid or unchanged).
  onErrorChange?: (error: string | null) => void;
  readOnly: boolean;
  ChangedIndicator: () => JSX.Element;
}

let nextRowId = 0;

const asOfficeKeys = (value: unknown): OfficeKeys =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as OfficeKeys) : {};

const toRows = (value: unknown, originalValue: unknown): OfficeKeyRow[] => {
  const stored = asOfficeKeys(originalValue);
  return Object.entries(asOfficeKeys(value)).map(([label, key]) => ({
    id: nextRowId++,
    label,
    key: key ?? "",
    storedLabel: label in stored ? label : undefined,
  }));
};

// Only called for rows that passed validateOfficeKeys, so labels cannot collide after trimming.
const toOfficeKeys = (rows: OfficeKeyRow[]): OfficeKeys =>
  Object.fromEntries(rows.map((row) => [row.label.trim(), row.key]));

const validateOfficeKeys = (rows: OfficeKeyRow[]): string | null => {
  if (rows.length === 0) return "Add at least one office API key.";
  const seen = new Set<string>();
  for (const row of rows) {
    const label = row.label.trim();
    if (!label) return "Every office needs a label.";
    if (label.includes(".")) return `Office label "${label}" cannot contain a dot (.).`;
    if (seen.has(label)) return `Office label "${label}" is used more than once.`;
    seen.add(label);
    if (!row.key.trim()) return `Enter the API key for office "${label}".`;
  }
  return null;
};

const isDirty = (rows: OfficeKeyRow[], originalValue: unknown): boolean =>
  JSON.stringify(rows.map((row) => [row.label, row.key])) !==
  JSON.stringify(Object.entries(asOfficeKeys(originalValue)).map(([label, key]) => [label, key ?? ""]));

// What to push into the company state for the next rows: nothing while invalid, the original
// value again once the rows match the stored ones, otherwise the edited office map.
const decidePush = (rows: OfficeKeyRow[], originalValue: unknown): { value: OfficeKeys | null } | null => {
  if (!isDirty(rows, originalValue)) return { value: (originalValue ?? null) as OfficeKeys | null };
  return validateOfficeKeys(rows) ? null : { value: toOfficeKeys(rows) };
};

// Editor for Zabun360's api_key: one API key per office. Keys arrive masked from Compass;
// unchanged rows stay masked so the deep-diff save does not send them.
function OfficeKeysEditor({
  value,
  originalValue,
  onChange,
  onErrorChange,
  readOnly,
  ChangedIndicator,
}: OfficeKeysEditorProps) {
  const [rows, setRows] = useState<OfficeKeyRow[]>(() => toRows(value, originalValue));
  const originalJson = JSON.stringify(originalValue ?? null);
  const valueJson = JSON.stringify(value ?? null);
  const lastPushed = useRef(valueJson);
  const lastOriginal = useRef(originalJson);

  // Reload the rows on an external change: a new value from Compass (load, save) or from the parent (revert).
  // Values pushed by this editor are recorded in lastPushed and ignored; this never calls onChange.
  useEffect(() => {
    if (valueJson === lastPushed.current && originalJson === lastOriginal.current) return;
    lastPushed.current = valueJson;
    lastOriginal.current = originalJson;
    setRows(toRows(value, originalValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueJson, originalJson]);

  const dirty = isDirty(rows, originalValue);
  const error = dirty ? validateOfficeKeys(rows) : null;

  useEffect(() => {
    onErrorChange?.(error);
  }, [error, onErrorChange]);

  // A stale error must not block saving once the editor is gone (e.g. another integration is selected).
  useEffect(() => () => onErrorChange?.(null), [onErrorChange]);

  const update = (next: OfficeKeyRow[]) => {
    setRows(next);
    // Invalid rows stay visible (with the error) but are never pushed into the company state.
    const push = decidePush(next, originalValue);
    if (!push) return;
    lastPushed.current = JSON.stringify(push.value ?? null);
    onChange(push.value);
  };

  const setLabel = (id: number, label: string) =>
    update(
      rows.map((row) =>
        row.id !== id
          ? row
          : {
              ...row,
              label,
              // The masked key belongs to the stored label: a renamed office needs its key re-entered.
              key: row.key === MASKED_CREDENTIAL && label.trim() !== row.storedLabel ? "" : row.key,
            },
      ),
    );

  const setKey = (id: number, key: string) => update(rows.map((row) => (row.id === id ? { ...row, key } : row)));

  // Clear a masked key on focus so a new key can be typed (same behaviour as the other credential fields).
  const focusKey = (id: number) =>
    update(rows.map((row) => (row.id === id && row.key === MASKED_CREDENTIAL ? { ...row, key: "" } : row)));

  // Leaving a stored office's key empty restores the masked (unchanged) key.
  const blurKey = (id: number) =>
    update(
      rows.map((row) =>
        row.id === id && row.key === "" && row.storedLabel !== undefined && row.label.trim() === row.storedLabel
          ? { ...row, key: MASKED_CREDENTIAL }
          : row,
      ),
    );

  return (
    <Form.Group className="mb-4">
      <Form.Label className="d-block">
        Office API keys
        {dirty && <ChangedIndicator />}
      </Form.Label>
      {rows.map((row) => (
        <InputGroup key={row.id} className="mb-2">
          <Form.Control
            type="text"
            value={row.label}
            onChange={(e) => setLabel(row.id, e.target.value)}
            placeholder="Office (e.g. Brugge)"
            readOnly={readOnly}
            style={{ maxWidth: "40%" }}
          />
          <Form.Control
            type="password"
            value={row.key}
            onChange={(e) => setKey(row.id, e.target.value)}
            onFocus={() => !readOnly && focusKey(row.id)}
            onBlur={() => !readOnly && blurKey(row.id)}
            placeholder="API key"
            autoComplete="new-password"
            readOnly={readOnly}
          />
          {!readOnly && (
            <Button
              variant="outline-danger"
              onClick={() => update(rows.filter((r) => r.id !== row.id))}
              title="Remove office"
            >
              <i className="bi bi-trash"></i>
            </Button>
          )}
        </InputGroup>
      ))}
      {!readOnly && (
        <div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => update([...rows, { id: nextRowId++, label: "", key: "" }])}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Add office
          </Button>
        </div>
      )}
      {error && <Form.Text className="text-danger d-block mt-2">{error}</Form.Text>}
      <Form.Text className="text-muted d-block mt-2">
        One Zabun360 API key per office. Office labels are for display only.
      </Form.Text>
    </Form.Group>
  );
}

export default OfficeKeysEditor;
